import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, doc, getDoc, query, where, Timestamp } from "firebase/firestore";
import emailjs from "@emailjs/browser";

const DOC_TYPES = [
  { value: "有給申請",   label: "有給申請",   timeMode: "range",  timeLabel: ["開始時間", "終了時間"] },
  { value: "欠勤届",     label: "欠勤届",     timeMode: "range",  timeLabel: ["開始時間", "終了時間"] },
  { value: "慶弔申請",   label: "慶弔申請",   timeMode: "single", timeLabel: ["時間"] },
  { value: "出勤変更届", label: "出勤変更届", timeMode: "arrow",  timeLabel: ["変更前", "変更後"] },
];

// 申請ではなく書類だけを送るときの種別
const SEND_TYPE = "書類送付";

const EMPTY_FORM = { type: "有給申請", date: "", timeFrom: "", timeTo: "", reason: "" };
// Firestoreドキュメントの上限は1MB。base64エンコードで約33%増えるため700KBに制限
const MAX_FILE_SIZE = 700 * 1024; // 700KB

function Document({ user, isAdmin, companyId }) {
  const [docs, setDocs]         = useState([]);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [attachment, setAttachment] = useState(null); // { name, type, data(base64) }
  const [fileError, setFileError]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab]           = useState("list");
  const [sendTitle, setSendTitle] = useState("");
  const [sendMemo, setSendMemo]   = useState("");
  const fileInputRef = useRef();

  const typeDef = DOC_TYPES.find(t => t.value === form.type) || DOC_TYPES[0];

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      let q;
      if (isAdmin && companyId) {
        q = query(collection(db, "documents"), where("companyId", "==", companyId));
      } else if (isAdmin) {
        q = collection(db, "documents");
      } else {
        q = query(collection(db, "documents"), where("uid", "==", user.uid));
      }
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.createdAt - a.createdAt);
      setDocs(data);
    } catch (e) { console.error(e); }
  };

  const buildDateStr = () => {
    const { timeMode } = typeDef;
    const base = form.date;
    if (timeMode === "range") {
      const t = [form.timeFrom, form.timeTo].filter(Boolean).join(" 〜 ");
      return t ? `${base}　${t}` : base;
    }
    if (timeMode === "arrow") {
      const t = [form.timeFrom, form.timeTo].filter(Boolean).join(" → ");
      return t ? `${base}　${t}` : base;
    }
    if (timeMode === "single") {
      return form.timeFrom ? `${base}　${form.timeFrom}` : base;
    }
    return base;
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileError("");
    if (file.size > MAX_FILE_SIZE) {
      setFileError("ファイルは700KB以下にしてください");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAttachment({ name: file.name, type: file.type, data: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const clearAttachment = () => {
    setAttachment(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // タブを移動したら選択中のファイルは持ち越さない
  const switchTab = (next) => {
    clearAttachment();
    setTab(next);
  };

  // メール通知：会社設定（on/off・宛先）＋システム設定（EmailJS認証情報）
  const notifyAdmin = async ({ docType, docDate, docReason }) => {
    try {
      const nid = companyId || "default";
      const [compSnap, sysSnap] = await Promise.all([
        getDoc(doc(db, "notifySettings", nid)),
        getDoc(doc(db, "notifySettings", "system")),
      ]);
      const comp = compSnap.exists() ? compSnap.data() : {};
      const sys  = sysSnap.exists()  ? sysSnap.data()  : {};
      if (comp.notifyOnDocument && comp.notifyEmail &&
          sys.emailjsServiceId && sys.emailjsTemplateId && sys.emailjsPublicKey) {
        await emailjs.send(
          sys.emailjsServiceId,
          sys.emailjsTemplateId,
          {
            to_email:   comp.notifyEmail,
            from_name:  user.email,
            doc_type:   docType,
            doc_date:   docDate || "（なし）",
            doc_reason: docReason || "（記載なし）",
          },
          sys.emailjsPublicKey
        );
      }
    } catch (err) {
      console.warn("メール通知の送信に失敗しました:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) { alert("日付を入力してください"); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "documents"), {
        uid: user.uid,
        email: user.email,
        companyId: companyId || "",
        type: form.type,
        date: buildDateStr(),
        reason: form.reason,
        attachment: attachment || null,
        status: "pending",
        createdAt: Timestamp.now(),
      });

      await notifyAdmin({ docType: form.type, docDate: buildDateStr(), docReason: form.reason });

      setForm(EMPTY_FORM);
      clearAttachment();
      setTab("list");
      fetchDocs();
    } catch (err) {
      console.error("書類申請の送信に失敗しました:", err);
      alert("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  // 申請ではなく、書類だけを会社へ送る
  const handleSendDoc = async (e) => {
    e.preventDefault();
    if (!attachment) { alert("送付するファイルを選択してください"); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "documents"), {
        uid: user.uid,
        email: user.email,
        companyId: companyId || "",
        type: SEND_TYPE,
        title: sendTitle,
        date: "",
        reason: sendMemo,
        attachment,
        status: "pending",
        createdAt: Timestamp.now(),
      });

      await notifyAdmin({ docType: SEND_TYPE, docDate: "", docReason: sendTitle || sendMemo });

      setSendTitle("");
      setSendMemo("");
      clearAttachment();
      setTab("list");
      fetchDocs();
    } catch (err) {
      console.error("書類の送付に失敗しました:", err);
      alert("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async (id, status) => {
    await updateDoc(doc(db, "documents", id), { status });
    fetchDocs();
  };

  // 添付ファイルをダウンロード
  const downloadFile = (att) => {
    const a = document.createElement("a");
    a.href = att.data;
    a.download = att.name;
    a.click();
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const fmt = (ts) => ts?.toDate().toLocaleDateString("ja-JP");
  const statusColor = (s) => s === "approved" ? "#27ae60" : s === "rejected" ? "#e74c3c" : "#f39c12";
  const isSendType  = (t) => t === SEND_TYPE;
  const statusLabel = (s, t) => isSendType(t)
    ? (s === "approved" ? "受領済" : s === "rejected" ? "差し戻し" : "未確認")
    : (s === "approved" ? "承認済" : s === "rejected" ? "却下"     : "審査中");

  // ファイルアイコン
  const fileIcon = (type = "") => {
    if (type.includes("pdf")) return "📄";
    if (type.includes("image")) return "🖼️";
    if (type.includes("word") || type.includes("document")) return "📝";
    if (type.includes("sheet") || type.includes("excel")) return "📊";
    return "📎";
  };

  // 添付ファイルの選択UI（新規申請・書類送付の両方で使う）
  const renderFilePicker = (labelText, note) => (
    <>
      <label style={st.label}>{labelText} <span style={st.optional}>{note}</span></label>
      {attachment ? (
        <div style={st.filePreview}>
          <span style={{ fontSize: "20px" }}>{fileIcon(attachment.type)}</span>
          <span style={{ flex: 1, fontSize: "14px", color: "#2c3e50", wordBreak: "break-all" }}>{attachment.name}</span>
          <button type="button" style={st.fileRemoveBtn} onClick={clearAttachment}>✕ 削除</button>
        </div>
      ) : (
        <label style={st.fileLabel}>
          <span style={{ fontSize: "20px" }}>📎</span>
          <span style={{ fontSize: "14px", color: "#2c3e50" }}>ファイルを選択</span>
          <span style={{ fontSize: "12px", color: "#999" }}>PDF・画像・Word・Excel など</span>
          <input ref={fileInputRef} type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
            style={{ display: "none" }} onChange={handleFile} />
        </label>
      )}
      {fileError && <p style={{ color: "#e74c3c", fontSize: "13px", marginBottom: "12px" }}>{fileError}</p>}
    </>
  );

  return (
    <div style={st.container}>
      <h2 style={st.title}>📄 書類申請</h2>
      <div style={st.tabs}>
        <button style={{ ...st.tab, ...(tab === "list" ? st.active : {}) }} onClick={() => switchTab("list")}>一覧</button>
        <button style={{ ...st.tab, ...(tab === "new"  ? st.active : {}) }} onClick={() => switchTab("new")}>新規申請</button>
        <button style={{ ...st.tab, ...(tab === "send" ? st.active : {}) }} onClick={() => switchTab("send")}>書類を送付</button>
      </div>

      {tab === "new" && (
        <form onSubmit={handleSubmit} style={st.form}>

          {/* 申請種別 */}
          <label style={st.label}>申請種別</label>
          <div style={st.typeGrid}>
            {DOC_TYPES.map(t => (
              <button key={t.value} type="button"
                style={{ ...st.typeBtn, ...(form.type === t.value ? st.typeBtnActive : {}) }}
                onClick={() => { set("type", t.value); set("timeFrom", ""); set("timeTo", ""); }}
              >{t.label}</button>
            ))}
          </div>

          {/* 日付 */}
          <label style={st.label}>日付</label>
          <input style={st.input} type="date" value={form.date}
            onChange={e => set("date", e.target.value)} required />

          {/* 時間 */}
          {typeDef.timeMode === "range" && (
            <>
              <label style={st.label}>{typeDef.timeLabel[0]}　〜　{typeDef.timeLabel[1]}</label>
              <div style={st.timeRow}>
                <input style={{ ...st.input, ...st.timeInput }} type="time"
                  value={form.timeFrom} onChange={e => set("timeFrom", e.target.value)} />
                <span style={st.timeSep}>〜</span>
                <input style={{ ...st.input, ...st.timeInput }} type="time"
                  value={form.timeTo} onChange={e => set("timeTo", e.target.value)} />
              </div>
            </>
          )}
          {typeDef.timeMode === "arrow" && (
            <>
              <label style={st.label}>{typeDef.timeLabel[0]}　→　{typeDef.timeLabel[1]}</label>
              <div style={st.timeRow}>
                <input style={{ ...st.input, ...st.timeInput }} type="time"
                  value={form.timeFrom} onChange={e => set("timeFrom", e.target.value)} />
                <span style={st.timeSep}>→</span>
                <input style={{ ...st.input, ...st.timeInput }} type="time"
                  value={form.timeTo} onChange={e => set("timeTo", e.target.value)} />
              </div>
            </>
          )}
          {typeDef.timeMode === "single" && (
            <>
              <label style={st.label}>{typeDef.timeLabel[0]}</label>
              <input style={st.input} type="time"
                value={form.timeFrom} onChange={e => set("timeFrom", e.target.value)} />
            </>
          )}

          {/* 理由 */}
          <label style={st.label}>理由</label>
          <textarea style={{ ...st.input, height: "100px", resize: "vertical" }}
            value={form.reason} onChange={e => set("reason", e.target.value)}
            placeholder="申請理由を入力してください" required />

          {/* ファイル添付 */}
          {renderFilePicker("添付ファイル", "（任意・700KB以下）")}

          <button style={{ ...st.submitBtn, marginTop: "8px" }} type="submit" disabled={submitting}>
            {submitting ? "送信中..." : "申請する"}
          </button>
        </form>
      )}

      {tab === "send" && (
        <form onSubmit={handleSendDoc} style={st.form}>
          <p style={st.sendLead}>申請ではなく、書類だけを会社へ送ります。</p>

          <label style={st.label}>件名 <span style={st.optional}>（任意）</span></label>
          <input style={st.input} type="text" value={sendTitle}
            onChange={e => setSendTitle(e.target.value)}
            placeholder="例：健康診断結果" />

          {renderFilePicker("送付するファイル", "（700KB以下）")}

          <label style={st.label}>メモ <span style={st.optional}>（任意）</span></label>
          <textarea style={{ ...st.input, height: "100px", resize: "vertical" }}
            value={sendMemo} onChange={e => setSendMemo(e.target.value)}
            placeholder="伝えたいことがあれば入力してください" />

          <button style={st.submitBtn} type="submit" disabled={submitting}>
            {submitting ? "送信中..." : "送付する"}
          </button>
        </form>
      )}

      {tab === "list" && (
        docs.length === 0
          ? <p style={st.empty}>申請書類はありません</p>
          : docs.map(d => (
            <div key={d.id} style={st.card}>
              <div style={st.cardTop}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={st.docType}>{d.type}</span>
                  {isAdmin && <span style={{ fontSize: "13px", color: "#7f8c8d" }}>{d.email}</span>}
                </div>
                <span style={{ ...st.badge, backgroundColor: statusColor(d.status) + "22", color: statusColor(d.status) }}>{statusLabel(d.status, d.type)}</span>
              </div>
              {d.title  && <div style={st.infoRow}><span style={st.infoLabel}>件名</span>{d.title}</div>}
              {d.date   && <div style={st.infoRow}><span style={st.infoLabel}>日時</span>{d.date}</div>}
              {d.reason && <div style={st.infoRow}><span style={st.infoLabel}>{isSendType(d.type) ? "メモ" : "理由"}</span>{d.reason}</div>}
              {d.attachment && (
                <div style={st.infoRow}>
                  <span style={st.infoLabel}>添付</span>
                  <button type="button" style={st.downloadBtn} onClick={() => downloadFile(d.attachment)}>
                    {fileIcon(d.attachment.type)} {d.attachment.name}
                  </button>
                </div>
              )}
              <div style={st.meta}>{isSendType(d.type) ? "送付日" : "申請日"}：{fmt(d.createdAt)}</div>
              {isAdmin && d.status === "pending" && (
                <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                  <button style={st.approveBtn} onClick={() => handleStatus(d.id, "approved")}>{isSendType(d.type) ? "受領" : "承認"}</button>
                  <button style={st.rejectBtn}  onClick={() => handleStatus(d.id, "rejected")}>{isSendType(d.type) ? "差し戻し" : "却下"}</button>
                </div>
              )}
            </div>
          ))
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "24px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  active: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
  label: { display: "block", marginBottom: "8px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  typeGrid: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" },
  typeBtn: { padding: "10px 18px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d", fontWeight: "500" },
  typeBtnActive: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff" },
  timeRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" },
  timeInput: { flex: 1, marginBottom: 0 },
  timeSep: { fontSize: "18px", color: "#7f8c8d", fontWeight: "bold", flexShrink: 0 },
  submitBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  // ファイル添付
  fileLabel: { display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", border: "2px dashed #ccd", borderRadius: "10px", padding: "20px", cursor: "pointer", backgroundColor: "#fafbfc", marginBottom: "16px", textAlign: "center" },
  filePreview: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", backgroundColor: "#f0f8f4", borderRadius: "8px", marginBottom: "16px", border: "1px solid #c3e6cb" },
  fileRemoveBtn: { padding: "4px 12px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "12px", flexShrink: 0 },
  optional: { fontWeight: "normal", color: "#999", fontSize: "12px" },
  sendLead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.7", marginTop: 0, marginBottom: "20px" },
  downloadBtn: { background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "14px", textDecoration: "underline", padding: 0, textAlign: "left" },
  // 一覧
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" },
  docType: { display: "inline-block", backgroundColor: "#f0f4f8", color: "#2c3e50", fontSize: "12px", fontWeight: "bold", padding: "3px 10px", borderRadius: "4px" },
  badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold", whiteSpace: "nowrap" },
  infoRow: { fontSize: "14px", color: "#555", marginBottom: "6px", display: "flex", gap: "8px", alignItems: "flex-start" },
  infoLabel: { color: "#999", fontSize: "13px", minWidth: "36px", flexShrink: 0, paddingTop: "2px" },
  meta: { fontSize: "12px", color: "#bbb", marginTop: "8px" },
  approveBtn: { padding: "8px 20px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  rejectBtn:  { padding: "8px 20px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
};

export default Document;
